/**
 * Hook para Gerenciamento de Cache de Mídias - Netflix-style Offline Storage
 * 
 * Este hook gerencia o ciclo de vida completo do cache:
 * 1. Verifica se mídia existe no cache local
 * 2. Se existe, usa a versão em cache (economiza banda)
 * 3. Se não existe ou mudou, baixa e salva no cache
 * 4. Limpa mídias antigas automaticamente
 * 5. Fornece URLs blob para reprodução offline
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    getMediaFromCache,
    saveMediaToCache,
    deleteMediaFromCache,
    getAllMediasFromCache,
    getCacheStats,
    cleanupOldCache,
    isMediaCached,
    isCacheSupported,
    hasCacheSpace,
    forceFreeSpace,
    getCachedMediaInfo,
    initializePersistentCache,
    getPersistentStorageStatus,
    formatBytes,
    type PersistentStorageStatus
} from './mediaCache';
import { isYouTubeUrl } from './youtube';

// Interface para mídia com URL local
export interface MediaWithLocalUrl {
    id: string;
    title: string;
    url: string;           // URL original
    localUrl: string;      // URL blob local (ou URL original se não cacheável)
    type: string;
    display_time?: number;
    active: boolean;
    selected_screens: string[];
    isCached: boolean;     // Indica se está usando cache
    isLoading: boolean;    // Indica se está baixando
    cacheError?: string;   // Erro de cache (se houver)
}

// Status do cache
export interface CacheStatus {
    isSupported: boolean;
    isInitialized: boolean;
    isPersistent: boolean;      // NOVO: Se o armazenamento é persistente
    totalCachedMB: string;
    totalCachedCount: number;
    isProcessing: boolean;
    processingProgress: number; // 0-100
    errors: string[];
    storageQuota?: string;      // NOVO: Quota total de armazenamento
    storageUsage?: string;      // NOVO: Uso atual
    storageAvailable?: string;  // NOVO: Espaço disponível
}

// Tipos de mídia que suportam cache
const CACHEABLE_TYPES = ['image', 'video'];
const CACHEABLE_EXTENSIONS = [
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp',
    'mp4', 'webm', 'ogg', 'mov', 'm4v'
];

/**
 * Verifica se uma URL pode ser cacheada
 */
function isCacheableUrl(url: string): boolean {
    if (!url) return false;

    // YouTube/Vimeo usam embed, não cache
    if (isYouTubeUrl(url)) return false;
    if (url.includes('vimeo.com')) return false;

    // Verificar extensão
    const extension = url.toLowerCase().split('.').pop()?.split('?')[0] || '';
    return CACHEABLE_EXTENSIONS.includes(extension);
}

/**
 * Detecta o tipo de mídia pela URL
 */
function detectMediaType(url: string): 'image' | 'video' | 'other' {
    const extension = url.toLowerCase().split('.').pop()?.split('?')[0] || '';

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'wmv', 'flv'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    return 'other';
}

/**
 * Hook principal para gerenciamento de cache de mídias
 * @param medias Array de mídias para processar
 * @param disableCache Se true, ignora completamente o cache e usa URLs originais (modo preview)
 */
export function useMediaCache(medias: any[], disableCache: boolean = false): {
    mediasWithCache: MediaWithLocalUrl[];
    cacheStatus: CacheStatus;
    refreshCache: () => Promise<void>;
    clearAllCache: () => Promise<void>;
    getCacheInfo: () => Promise<any>;
} {
    // ==========================================================================
    // IMPORTANTE: Inicializar mediasWithCache com as mídias originais
    // Isso garante que as mídias apareçam IMEDIATAMENTE usando URLs originais
    // Depois são atualizadas com URLs do cache quando disponíveis
    // ==========================================================================

    // Converte mídias para formato com URLs locais (inicialmente usando URL original)
    const initialMedias = useMemo(() => {
        if (!medias || medias.length === 0) return [];
        return medias.map(m => ({
            id: m.id,
            title: m.title || 'Mídia sem título',
            url: m.url,
            localUrl: m.url, // Inicialmente usa URL original
            type: m.type || detectMediaType(m.url),
            display_time: m.display_time,
            active: m.active ?? true,
            selected_screens: m.selected_screens || [],
            isCached: false,
            isLoading: false
        }));
    }, [medias]);

    // Estado das mídias com URLs locais - INICIA com mídias originais
    const [mediasWithCache, setMediasWithCache] = useState<MediaWithLocalUrl[]>([]);

    // CRÍTICO: Sincronizar imediatamente quando medias mudar
    // Isso garante que as mídias apareçam INSTANTANEAMENTE usando URLs originais
    // Depois o cache processa em background e atualiza com URLs locais (blob)
    useEffect(() => {
        if (initialMedias.length > 0) {
            console.log('📺 [CACHE] Sincronizando mídias originais imediatamente:', initialMedias.length);
            // Preservar URLs de cache existentes se a mídia já foi processada
            setMediasWithCache(prev => {
                if (prev.length === 0) {
                    // Primeira vez: usar todas as mídias originais
                    return initialMedias;
                }
                // Atualização: mesclar mídias novas mantendo URLs de cache existentes
                return initialMedias.map(newMedia => {
                    const existing = prev.find(m => m.id === newMedia.id);
                    if (existing && existing.isCached && existing.localUrl !== existing.url) {
                        // Manter URL de cache se já processada
                        return { ...newMedia, localUrl: existing.localUrl, isCached: true };
                    }
                    return newMedia;
                });
            });
        }
    }, [initialMedias]);

    // Status do cache
    const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
        isSupported: isCacheSupported(),
        isInitialized: false,
        isPersistent: false,
        totalCachedMB: '0',
        totalCachedCount: 0,
        isProcessing: false,
        processingProgress: 0,
        errors: []
    });

    // Ref para controlar se já inicializou persistent storage
    const persistentInitializedRef = useRef<boolean>(false);

    // Ref para URLs blob criadas (para cleanup)
    const blobUrlsRef = useRef<Map<string, string>>(new Map());

    // Ref para evitar processamento duplicado
    const processingRef = useRef<boolean>(false);

    // Ref para track de mídias já processadas
    const processedIdsRef = useRef<Set<string>>(new Set());

    /**
     * Baixa uma mídia e salva no cache
     */
    const downloadAndCache = useCallback(async (
        id: string,
        url: string,
        title: string
    ): Promise<Blob | null> => {
        try {
            console.log(`⬇️ [CACHE] Baixando: ${title}`);

            // Verificar espaço disponível
            const hasSpace = await hasCacheSpace(50 * 1024 * 1024); // Assume 50MB
            if (!hasSpace) {
                console.log('⚠️ [CACHE] Espaço insuficiente, liberando...');
                await forceFreeSpace(100 * 1024 * 1024); // Libera 100MB
            }

            // Tentar fetch com diferentes configurações
            let response: Response;
            try {
                response = await fetch(url, {
                    method: 'GET',
                    cache: 'no-cache', // Força download fresco
                    credentials: 'omit'
                });
            } catch (corsError) {
                // Fallback para fetch simples
                console.log('⚠️ [CACHE] CORS error, tentando fetch simples...');
                response = await fetch(url);
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();

            // Salvar no cache
            await saveMediaToCache(id, blob, url, title);

            console.log(`✅ [CACHE] Salvo com sucesso: ${title} (${(blob.size / 1024 / 1024).toFixed(2)}MB)`);

            return blob;
        } catch (error) {
            console.error(`❌ [CACHE] Erro ao baixar ${title}:`, error);
            return null;
        }
    }, []);

    /**
     * Processa uma mídia individual
     */
    const processMedia = useCallback(async (media: any): Promise<MediaWithLocalUrl> => {
        const baseMedia: MediaWithLocalUrl = {
            id: media.id,
            title: media.title || 'Mídia sem título',
            url: media.url,
            localUrl: media.url, // Default: usa URL original
            type: media.type || detectMediaType(media.url),
            display_time: media.display_time,
            active: media.active ?? true,
            selected_screens: media.selected_screens || [],
            isCached: false,
            isLoading: false
        };

        // Se não é cacheável, retorna com URL original
        if (!isCacheableUrl(media.url)) {
            return baseMedia;
        }

        // Verificar se já foi processado e tem URL blob
        const existingBlobUrl = blobUrlsRef.current.get(media.id);
        if (existingBlobUrl) {
            return {
                ...baseMedia,
                localUrl: existingBlobUrl,
                isCached: true
            };
        }

        try {
            // Marcar como carregando
            baseMedia.isLoading = true;

            // Tentar buscar do cache primeiro
            let blob = await getMediaFromCache(media.id);

            // Se não está no cache, baixar
            if (!blob) {
                blob = await downloadAndCache(media.id, media.url, media.title);
            } else {
                console.log(`🎯 [CACHE] Cache hit: ${media.title}`);
            }

            // Se temos o blob, criar URL local
            if (blob) {
                const localUrl = URL.createObjectURL(blob);
                blobUrlsRef.current.set(media.id, localUrl);

                return {
                    ...baseMedia,
                    localUrl,
                    isCached: true,
                    isLoading: false
                };
            }

            // Fallback: usar URL original (garantir que localUrl = url)
            console.log(`⚠️ [CACHE] Fallback para URL original: ${media.title}`);
            return {
                ...baseMedia,
                localUrl: media.url, // Explicitamente usar URL original
                isCached: false,
                isLoading: false,
                cacheError: 'Não foi possível cachear'
            };

        } catch (error) {
            console.error(`❌ [CACHE] Erro processando ${media.title}:`, error);
            // Em caso de erro, garantir que localUrl = url original
            return {
                ...baseMedia,
                localUrl: media.url, // Explicitamente usar URL original
                isCached: false,
                isLoading: false,
                cacheError: error instanceof Error ? error.message : 'Erro desconhecido'
            };
        }
    }, [downloadAndCache]);

    /**
     * Processa todas as mídias
     */
    const processAllMedias = useCallback(async () => {
        if (!medias || medias.length === 0) return;
        if (processingRef.current) return;

        processingRef.current = true;

        setCacheStatus(prev => ({
            ...prev,
            isProcessing: true,
            processingProgress: 0,
            errors: []
        }));

        console.log(`🔄 [CACHE] Iniciando processamento de ${medias.length} mídias...`);

        try {
            // Limpar cache de mídias não utilizadas
            const activeIds = medias.map(m => m.id);
            await cleanupOldCache(activeIds);

            // Processar cada mídia
            const processedMedias: MediaWithLocalUrl[] = [];
            const errors: string[] = [];

            for (let i = 0; i < medias.length; i++) {
                const media = medias[i];

                try {
                    const processed = await processMedia(media);
                    processedMedias.push(processed);
                    processedIdsRef.current.add(media.id);
                } catch (error) {
                    console.error(`❌ [CACHE] Erro em ${media.title}:`, error);
                    errors.push(`${media.title}: ${error}`);

                    // Adicionar com URL original em caso de erro
                    processedMedias.push({
                        id: media.id,
                        title: media.title,
                        url: media.url,
                        localUrl: media.url,
                        type: media.type,
                        display_time: media.display_time,
                        active: media.active ?? true,
                        selected_screens: media.selected_screens || [],
                        isCached: false,
                        isLoading: false,
                        cacheError: error instanceof Error ? error.message : 'Erro'
                    });
                }

                // Atualizar progresso
                const progress = Math.round(((i + 1) / medias.length) * 100);
                setCacheStatus(prev => ({
                    ...prev,
                    processingProgress: progress
                }));
            }

            // Atualizar estado
            setMediasWithCache(processedMedias);

            // Atualizar estatísticas
            const stats = await getCacheStats();
            setCacheStatus(prev => ({
                ...prev,
                isInitialized: true,
                isProcessing: false,
                processingProgress: 100,
                totalCachedMB: stats.totalSizeMB,
                totalCachedCount: stats.mediaCount,
                errors
            }));

            // Emitir evento para atualizar contador na UI em tempo real
            window.dispatchEvent(new CustomEvent('cacheCountUpdate', {
                detail: { count: stats.mediaCount }
            }));

            console.log(`✅ [CACHE] Processamento concluído: ${processedMedias.filter(m => m.isCached).length}/${medias.length} em cache`);

        } catch (error) {
            console.error('❌ [CACHE] Erro no processamento:', error);
            setCacheStatus(prev => ({
                ...prev,
                isProcessing: false,
                errors: [...prev.errors, error instanceof Error ? error.message : 'Erro geral']
            }));
        } finally {
            processingRef.current = false;
        }
    }, [medias, processMedia]);

    /**
     * Força refresh do cache
     */
    const refreshCache = useCallback(async () => {
        // Limpar blob URLs existentes
        blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
        blobUrlsRef.current.clear();
        processedIdsRef.current.clear();

        // Reprocessar
        await processAllMedias();
    }, [processAllMedias]);

    /**
     * Limpa todo o cache
     */
    const clearAllCache = useCallback(async () => {
        try {
            // Revogar URLs blob
            blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
            blobUrlsRef.current.clear();
            processedIdsRef.current.clear();

            // Limpar IndexedDB
            const allCached = await getAllMediasFromCache();
            for (const cached of allCached) {
                await deleteMediaFromCache(cached.id);
            }

            // Resetar estado
            setMediasWithCache(medias.map(m => ({
                id: m.id,
                title: m.title,
                url: m.url,
                localUrl: m.url,
                type: m.type,
                display_time: m.display_time,
                active: m.active ?? true,
                selected_screens: m.selected_screens || [],
                isCached: false,
                isLoading: false
            })));

            setCacheStatus(prev => ({
                ...prev,
                totalCachedMB: '0',
                totalCachedCount: 0
            }));

            console.log('🧹 [CACHE] Cache completamente limpo');
        } catch (error) {
            console.error('❌ [CACHE] Erro ao limpar cache:', error);
        }
    }, [medias]);

    /**
     * Obtém informações do cache
     */
    const getCacheInfo = useCallback(async () => {
        return await getCacheStats();
    }, []);

    /**
     * NOVO: Efeito para inicializar armazenamento persistente Netflix-style
     * Solicita permissão para que o navegador NÃO apague os dados automaticamente
     */
    useEffect(() => {
        if (persistentInitializedRef.current) return;
        persistentInitializedRef.current = true;

        const initPersistentStorage = async () => {
            console.log('🚀 [CACHE] Inicializando armazenamento persistente Netflix-style...');

            try {
                // Inicializar o sistema de cache persistente
                const result = await initializePersistentCache();

                if (result.success) {
                    const { storageInfo } = result;

                    setCacheStatus(prev => ({
                        ...prev,
                        isPersistent: storageInfo.isPersisted,
                        storageQuota: storageInfo.quota ? formatBytes(storageInfo.quota) : undefined,
                        storageUsage: storageInfo.usage ? formatBytes(storageInfo.usage) : undefined,
                        storageAvailable: storageInfo.available ? formatBytes(storageInfo.available) : undefined
                    }));

                    if (storageInfo.isPersisted) {
                        console.log('✅ [CACHE] Armazenamento PERSISTENTE ativado! Mídias serão mantidas mesmo após fechar o navegador.');
                    } else {
                        console.warn('⚠️ [CACHE] Armazenamento NÃO persistente. O navegador pode limpar dados quando precisar de espaço.');
                    }
                } else {
                    console.error('❌ [CACHE] Falha na inicialização:', result.error);
                }
            } catch (error) {
                console.error('❌ [CACHE] Erro ao inicializar armazenamento persistente:', error);
            }
        };

        initPersistentStorage();
    }, []);


    /**
     * Efeito para processar mídias quando a lista muda
     */
    useEffect(() => {
        if (!cacheStatus.isSupported || disableCache) {
            if (disableCache) console.log('🛑 [CACHE] Cache desabilitado (Modo Preview)');
            else console.warn('⚠️ [CACHE] IndexedDB não suportado neste navegador');

            // Usar URLs originais
            setMediasWithCache(medias.map(m => ({
                id: m.id,
                title: m.title,
                url: m.url,
                localUrl: m.url,
                type: m.type,
                display_time: m.display_time,
                active: m.active ?? true,
                selected_screens: m.selected_screens || [],
                isCached: false,
                isLoading: false
            })));
            return;
        }

        // Processar mídias
        processAllMedias();

    }, [medias, cacheStatus.isSupported, disableCache, processAllMedias]);

    /**
     * Cleanup ao desmontar
     */
    useEffect(() => {
        return () => {
            // Revogar todas as URLs blob ao desmontar
            blobUrlsRef.current.forEach(url => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    // Ignorar erros de revogação
                }
            });
        };
    }, []);

    return {
        mediasWithCache,
        cacheStatus,
        refreshCache,
        clearAllCache,
        getCacheInfo
    };
}

export default useMediaCache;
